import dayjs from "dayjs";
import { toPng } from "html-to-image";
import JSZip from "jszip";
import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { FacilityReportData } from "../../../../models/FacilityReportData";

import "./styles.scss";

interface IDownloadButtonProps {
  facilityReportData: FacilityReportData;
  isWorking: boolean;
  setIsWorking: (isWorking: boolean) => void;
}

export const DownloadButton: React.FunctionComponent<IDownloadButtonProps> = ({
  facilityReportData,
  isWorking,
  setIsWorking,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityReportsPage.facilityReportDetails.downloadButton.${key}`);

  const sanitizedLocationName = facilityReportData.location.name
    .replace(/[/\\]+/g, "_")
    .replace(/\.\.+/g, "_")
    .trim();

  const sections = [
    {
      section: "search-keywords",
      id: "facility-report-details-search-keywords-section",
      fileName: `${t("facility_report")}-${sanitizedLocationName}-${t(
        "section.search_keywords"
      )}.png`,
    },
    {
      section: "website",
      id: "facility-report-details-website-section",
      fileName: `${t("facility_report")}-${sanitizedLocationName}-${t(
        "section.website"
      )}.png`,
    },
    {
      section: "calls",
      id: "facility-report-details-calls-section",
      fileName: `${t("facility_report")}-${sanitizedLocationName}-${t(
        "section.calls"
      )}.png`,
    },
    {
      section: "route-search",
      id: "facility-report-details-route-search-section",
      fileName: `${t("facility_report")}-${sanitizedLocationName}-${t(
        "section.route_search"
      )}.png`,
    },
    {
      section: "reviews",
      id: "facility-report-details-reviews-section",
      fileName: `${t("facility_report")}-${sanitizedLocationName}-${t(
        "section.reviews"
      )}.png`,
    },
    {
      section: "overall-ai-summary",
      id: "facility-report-details-overall-ai-summary-section",
      fileName: `${t("facility_report")}-${sanitizedLocationName}-${t(
        "section.overall_ai_summary"
      )}.png`,
    },
  ];

  const handleDownloadImage = async () => {
    setIsWorking(true);

    try {
      const zip = new JSZip();
      const imageFolder = zip.folder("images");

      for (const section of sections) {
        try {
          const node = document.getElementById(section.id);

          if (!node || !imageFolder) {
            continue;
          }

          const image = await toPng(node);
          const imageData = image.split(",")[1];
          imageFolder.file(section.fileName, imageData, {
            base64: true,
          });
        } catch (error) {
          console.error("Error downloading image:", error);
        }
      }

      const startDateString = dayjs(facilityReportData.start_date).format(
        "YYYY-MM-DD"
      );
      const endDateString = dayjs(facilityReportData.end_date).format(
        "YYYY-MM-DD"
      );
      const zipName = `${sanitizedLocationName}-${startDateString}〜${endDateString}.zip`;

      const blob = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = zipName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading images:", error);
      toast(t("error_downloading_images"), { type: "error" });
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <button
      className="btn btn-border download-button"
      onClick={handleDownloadImage}
      type="button"
      disabled={isWorking}
    >
      <span
        aria-hidden={isWorking}
        className={isWorking ? "download-button-hidden" : ""}
      >
        {t("download_image")}
      </span>
      <span
        aria-hidden={!isWorking}
        className={isWorking ? "" : "download-button-hidden"}
      >
        {t("downloading")}
      </span>
    </button>
  );
};
